import PageHeader from "@/components/PageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module, PATH_PROJECTS, PATH_ROOT } from "@/constants";
import { ApiError } from "@/services/api";
import { ReadProject, WriteProject } from "@/types/project";
import { useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ProjectCreatorForm from "./ProjectCreatorForm";
import { defaultCreationValues } from "../defaultValues";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { ProjectService } from "../service/project";

export default function ProjectCreator() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { invalidateListQuery } = useQueryCacheUpdater(Module.Projects);
  const { t } = useTranslation();
  const breadcrumbItems = useMemo(
    () => [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("projects.title.multiple"), href: PATH_PROJECTS },
      { label: t("projects.new") },
    ],
    [t],
  );

  const projectMutation = useMutation<ReadProject, ApiError, WriteProject>({
    mutationFn: ProjectService.post,
    onSuccess: (result) => {
      navigate(`${PATH_PROJECTS}/${result.id}`);
      toast({
        title: t("projects.success.create"),
      });
      invalidateListQuery();
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("projects.errors.create"),
        description,
        variant: "destructive",
      });
    },
  });

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <ProjectCreatorForm
        onSubmit={projectMutation.mutateAsync}
        project={defaultCreationValues}
        disabled={projectMutation.isPending}
      />
    </>
  );
}
